"use client";

import { useSession } from "next-auth/react";
import { useCallback } from "react";
import { logClientErrorAction } from "./error-actions";
import { ClientErrorData, ErrorCategory, ErrorSeverity } from "./error-types";

export function useErrorLogger() {
    const { data: session } = useSession();

    const logError = useCallback(
        async (
            error: Error,
            category: ErrorCategory = "javascript_error",
            severity: ErrorSeverity = "medium",
            componentStack?: string,
            additionalContext?: Record<string, any>
        ) => {
            // Only log for authenticated users
            if (!session) {
                return null;
            }

            const errorId = generateErrorId();

            const errorData: Omit<ClientErrorData, "userContext"> = {
                errorId,
                message: sanitizeErrorMessage(error.message),
                stack: sanitizeStackTrace(error.stack),
                componentStack: sanitizeStackTrace(componentStack),
                url: window.location.href,
                userAgent: navigator.userAgent,
                timestamp: Date.now(),
                category,
                severity,
                additionalContext: sanitizeAdditionalContext(additionalContext)
            };

            try {
                const response = await logClientErrorAction(errorData);
                if (!response.success) {
                    console.warn("Error logging failed:", response.message);
                }
                return response;
            } catch (logError) {
                console.error("Failed to log error:", logError);
                return null;
            }
        },
        [session]
    );

    const logReactError = useCallback(
        (
            error: Error,
            errorInfo: { componentStack?: string },
            severity: ErrorSeverity = "high"
        ) => {
            return logError(error, "react_error", severity, errorInfo.componentStack, {
                errorBoundary: true
            });
        },
        [logError]
    );

    const logNetworkError = useCallback(
        (error: Error, url: string, method: string, status?: number) => {
            return logError(
                error,
                "network_error",
                determineNetworkErrorSeverity(status),
                undefined,
                { networkUrl: url, method, status }
            );
        },
        [logError]
    );

    return {
        logError,
        logReactError,
        logNetworkError,
        isAuthenticated: !!session
    };
}

function generateErrorId(): string {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substring(2, 8);
    return `err_${timestamp}_${random}`;
}

function sanitizeErrorMessage(message: string): string {
    if (!message) return "Unknown error";

    // Remove potential sensitive data patterns
    return message
        .replace(/password[=:]\s*[^\s&]+/gi, "password=***")
        .replace(/token[=:]\s*[^\s&]+/gi, "token=***")
        .replace(/api[_-]?key[=:]\s*[^\s&]+/gi, "apikey=***")
        .replace(/bearer\s+[^\s&]+/gi, "bearer ***")
        .substring(0, 500); // Limit message length
}

function sanitizeStackTrace(stack?: string): string | undefined {
    if (!stack) return undefined;

    return stack
        .split("\n")
        .slice(0, 20) // Limit stack trace lines
        .map((line) => {
            // Remove file:// URLs and replace with relative paths
            return line.replace(/file:\/\/.*?\/([^/]+\/[^/]+)$/, ".../$1");
        })
        .join("\n");
}

function sanitizeAdditionalContext(context?: Record<string, any>): Record<string, any> | undefined {
    if (!context) return undefined;

    // Remove sensitive keys and limit depth
    const sanitized: Record<string, any> = {};
    const sensitiveKeys = ["password", "token", "apikey", "secret", "auth", "bearer"];

    for (const [key, value] of Object.entries(context)) {
        const lowerKey = key.toLowerCase();
        if (sensitiveKeys.some((sensitive) => lowerKey.includes(sensitive))) {
            sanitized[key] = "***";
        } else if (typeof value === "object" && value !== null) {
            sanitized[key] = "[Object]"; // Don't recurse deeply
        } else if (typeof value === "string") {
            sanitized[key] = value.substring(0, 100); // Limit string length
        } else {
            sanitized[key] = value;
        }
    }

    return sanitized;
}

function determineNetworkErrorSeverity(status?: number): ErrorSeverity {
    if (!status) return "medium";
    if (status >= 500) return "high";
    if (status >= 400) return "medium";
    return "low";
}
